/**
 * Builds the dynamic numbering string for a serie.
 * Mirrors backend `build_dynamic_number` in controllers/series/functions.py:
 *   parts = [prefix1, prefix2, paddedValue, suffix].filter(Boolean).join(separator)
 *
 * @param {object} args
 * @param {string} [args.prefix1]
 * @param {string} [args.prefix2]
 * @param {string} [args.suffix1]
 * @param {number} [args.padding=0]
 * @param {string} [args.separator] - Joiner between parts. `""` is valid (no separator). Defaults to "-".
 * @param {number|string} args.value - Numeric value to render
 * @returns {string}
 */
export const buildSerieNumber = ({
  prefix1 = "",
  prefix2 = "",
  suffix1 = "",
  padding = 0,
  separator,
  value,
}) => {
  const parts = []
  const p1 = (prefix1 ?? "").trim()
  const p2 = (prefix2 ?? "").trim()
  const sfx = (suffix1 ?? "").trim()
  const sep = separator ?? "-"
  const pad = Number.isFinite(Number(padding))
    ? Math.max(0, Number(padding))
    : 0

  if (p1) parts.push(p1)
  if (p2) parts.push(p2)
  if (value !== null && value !== undefined && value !== "") {
    const n = Number(value)
    const safe = Number.isFinite(n) ? n : 0
    parts.push(String(safe).padStart(pad, "0"))
  }
  if (sfx) parts.push(sfx)

  return parts.join(sep)
}

/**
 * Returns a preview of the next 3 emitted numbers for a serie.
 * `start` is the first user-visible number; we render start, start+1, start+2.
 * Used both in the row "Próximo N" column and inside the form modal.
 *
 * @param {object} args
 * @param {string} [args.prefix1]
 * @param {string} [args.prefix2]
 * @param {string} [args.suffix1]
 * @param {number} [args.padding=0]
 * @param {string} [args.separator] - Joiner between parts. `""` is valid (no separator). Defaults to "-".
 * @param {number} [args.start=1]
 * @returns {string} e.g. "FA-2025-00001-A, FA-2025-00002-A, FA-2025-00003-A..."
 */
export const buildSeriePreview = ({
  prefix1 = "",
  prefix2 = "",
  suffix1 = "",
  padding = 0,
  separator,
  start = 1,
}) => {
  const base = Number(start)
  const valid = Number.isFinite(base) && base >= 1
  const n1 = valid ? base : 1
  const n2 = n1 + 1
  const n3 = n1 + 2
  const make = v =>
    buildSerieNumber({
      prefix1,
      prefix2,
      suffix1,
      padding,
      separator,
      value: v,
    })
  return `${make(n1)}, ${make(n2)}, ${make(n3)}...`
}

/**
 * Builds the "next number" string for a serie row.
 * `value` is the last emitted number; the next will be value + 1.
 * If the serie has not started (value < start), the next is `start`.
 *
 * @param {object} serie - Serialized serie from backend.
 * @returns {string}
 */
export const buildSerieNextNumber = serie => {
  if (!serie) return ""
  const start = Number(serie.start ?? 1)
  const value = Number(serie.value ?? start - 1)
  const next = value + 1 < start ? start : value + 1
  return buildSerieNumber({
    prefix1: serie.prefix1,
    prefix2: serie.prefix2,
    suffix1: serie.suffix1,
    padding: serie.padding,
    separator: serie.separator,
    value: next,
  })
}

export const isSerieExhausted = serie => {
  if (!serie) return false
  const padding = Number(serie.padding ?? 0)
  if (!Number.isFinite(padding) || padding <= 0) return false
  const start = Number(serie.start ?? 1)
  const value = Number(serie.value ?? start - 1)
  const next = value + 1 < start ? start : value + 1
  const max = Math.pow(10, padding) - 1
  return next > max
}

export default buildSeriePreview
